import { CurrentOpenMenu } from '../types';
import { FilterAction } from '@/types/SearchFacet';
import {
    getCategoryUrlWithParams,
    getCollectionsUrlWithParams,
    getIsCollectionsPage,
    getIsSEOSearchPage,
    getIsSEOSearchPageWithLocation,
    getSearchUrlWithParams,
    getSeoSearchUrlWithLocationParams,
    getSeoSearchUrlWithParams,
    isCategoryPage,
    isPriceGuidePage,
} from '@/utils/urls';
import { modifyParamsRemovePage } from '@/utils/queryParams';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type NavigateWithParamsProps = {
    checkForPageRemoval?: boolean;
    currentOpenMenu?: CurrentOpenMenu;
    /**
     * The filter action that triggered this navigation, used for amplitude tracking
     */
    filterAction: FilterAction;
    params: any;
    preventScrollReset?: boolean;
    /** When true, triggers "Search Completed" instead of "Search Refined" in analytics */
    suggestionClicked?: boolean;
};

export const useNavigateSearchPageWithParams = () => {
    const navigate = useNavigate();

    const { pathname } = useLocation();

    const isSEOSearchPage = getIsSEOSearchPage(pathname);
    const isCollectionsPage = getIsCollectionsPage(pathname);
    const isOnCategoryPage = isCategoryPage(pathname);
    const isOnPriceGuidePage = isPriceGuidePage(pathname);

    return useCallback(
        ({
            checkForPageRemoval,
            currentOpenMenu,
            filterAction,
            params,
            preventScrollReset,
            suggestionClicked,
        }: NavigateWithParamsProps) => {
            let newParams = params;
            if (checkForPageRemoval && params?.page === 1) {
                newParams = modifyParamsRemovePage(newParams);
            }

            if (isSEOSearchPage || isCollectionsPage) {
                const pathSegments = pathname.split('/').filter((segment) => segment !== '');
                const keywordSlug = pathSegments.pop() ?? '';

                // If the pathname is a SEO search page, we need to add the keyword to the params from the slug
                newParams = {
                    ...newParams,
                    keyword: keywordSlug,
                };
            } else if (getIsSEOSearchPageWithLocation(pathname)) {
                const pathSegments = pathname.split('/').filter((segment) => segment !== '');
                const segments = {
                    citySlug: pathSegments?.[2] ?? '',
                    keyword: pathSegments?.[3] ?? '',
                    region: pathSegments?.[1] ?? '',
                };
                newParams = {
                    ...newParams,
                    citySlug: segments.citySlug,
                    keyword: segments.keyword,
                    region: segments.region,
                };
            }

            // This is to persist the current path when applying filters within the page
            if (isOnCategoryPage || isOnPriceGuidePage) {
                const url = getCategoryUrlWithParams(pathname, newParams);
                navigate(url, {
                    state: { currentOpenMenu, filterAction, preventScrollReset, suggestionClicked },
                });
            } else if (isCollectionsPage) {
                const url = getCollectionsUrlWithParams(newParams);
                navigate(url, {
                    state: { currentOpenMenu, filterAction, preventScrollReset, suggestionClicked },
                });
            } else if (isSEOSearchPage) {
                const url = getSeoSearchUrlWithParams(newParams);
                navigate(url, {
                    state: { currentOpenMenu, filterAction, preventScrollReset, suggestionClicked },
                });
            } else if (getIsSEOSearchPageWithLocation(pathname)) {
                const url = getSeoSearchUrlWithLocationParams(newParams);
                navigate(url, {
                    state: { currentOpenMenu, filterAction, preventScrollReset, suggestionClicked },
                });
            } else {
                const url = getSearchUrlWithParams(newParams);
                navigate(url, {
                    state: { currentOpenMenu, filterAction, preventScrollReset, suggestionClicked },
                });
            }
        },
        [isOnCategoryPage, isOnPriceGuidePage, isSEOSearchPage, isCollectionsPage, navigate, pathname]
    );
};
